import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { Trans, useTranslation } from 'react-i18next'
import type { ChapterReportView } from '@/types/chapterReport'
import { ScoreRing } from './ReportMetrics'
import { ReviewRecommendationEditor } from './ReviewRecommendationEditor'

interface Props {
  report: ChapterReportView
  selectedLessonIds: number[]
  editable?: boolean
  onRecommendationsChange?: (lessonIds: number[]) => void
}

function formatDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value.slice(0, 10)
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date).replace(/\.$/, '')
}

export function ChapterTestSection({
  report,
  selectedLessonIds,
  editable = false,
  onRecommendationsChange,
}: Props) {
  const { t } = useTranslation('coaching')
  const test = report.latestTest
  const selected = new Set(selectedLessonIds)

  return (
    <section className="chapter-report-print-section space-y-6" data-testid="chapter-test-section">
      <div className="flex items-center justify-between gap-5 px-2 pr-6">
        <div>
          <p className="text-base font-bold leading-[1.4] text-[#131313]">
            {t('chapterReport.testResultLead')}
          </p>
          <p className="text-xl font-bold leading-[1.4] tracking-[-0.6px] text-[#131313]">
            {t('chapterReport.testResultBefore')}
            <span className="text-[#4599ff]">
              {test.score == null
                ? t('chapterReport.testUnavailable')
                : t('chapterReport.testScore', { score: test.score })}
            </span>
            {test.score != null && t('chapterReport.testResultAfter')}
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs text-[#333]">
            <span className="rounded-full bg-[#eee] px-2.5 py-1 font-medium">
              {t('chapterReport.questionCount', { count: test.totalQuestionCount })}
            </span>
            <span className="inline-flex gap-2 rounded-full bg-[#eee] px-2.5 py-1">
              <strong className="text-[#888]">{t('chapterReport.diagnosedAt')}</strong>
              <span className="font-medium">{formatDate(test.completedAt)}</span>
            </span>
            {test.roundNo > 1 && (
              <span className="rounded-full bg-[#fff3dc] px-2.5 py-1 text-[#9a6500]">
                {t('chapterReport.round', { round: test.roundNo })}
              </span>
            )}
          </div>
        </div>
        <ScoreRing
          value={test.score}
          ariaLabel={test.score == null
            ? t('chapterReport.testUnavailable')
            : t('chapterReport.testScore', { score: test.score })}
        />
      </div>

      <div className="chapter-report-print-block grid gap-4 md:grid-cols-2">
        <div className="rounded-xl bg-[#fff5f5] px-5 py-4">
          <h3 className="text-sm font-bold tracking-[-0.6px] text-[#ff675e]">
            {t('chapterReport.wrongLessons')}
          </h3>
          <p className="mt-2 text-xs leading-[1.5] text-[#131313]">
            <Trans
              ns="coaching"
              i18nKey="chapterReport.wrongLessonsEditHelp"
              components={{
                review: <span className="text-[#ff675e]" />,
                pass: <span className="text-[#2ac47e]" />,
                lineBreak: <br />,
              }}
            />
          </p>
          {test.wrongQuestions.length === 0 ? (
            <p className="mt-4 rounded-lg bg-white px-3 py-2 text-xs text-[#888]">
              {t('chapterReport.wrongEmpty')}
            </p>
          ) : (
            <ul className="mt-4 space-y-2">
              {test.wrongQuestions.map((question) => (
                <li
                  key={question.questionNumber}
                  className="flex min-h-9 items-center justify-between gap-3 rounded-lg bg-white px-2.5 py-1.5 text-xs"
                >
                  <span className="min-w-0 text-[#333]">
                    {question.lessonNumber != null && <strong>{question.lessonNumber}. </strong>}
                    <ContentMetadataText text={question.lessonTitle || t('chapterReport.missing')} />
                  </span>
                  <span className="flex shrink-0 items-center gap-4">
                    <img src="/report/diagnostic/icon-X.svg" alt="" className="size-5" />
                    <span
                      className={`rounded-full px-2 py-1 font-semibold text-white ${
                        question.originLessonId != null && selected.has(question.originLessonId)
                          ? 'bg-[#ff675e]'
                          : 'bg-[#2ac47e]'
                      }`}
                    >
                      {question.originLessonId != null && selected.has(question.originLessonId)
                        ? t('chapterReport.review')
                        : t('chapterReport.pass')}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <ReviewRecommendationEditor
          candidates={report.recommendationCandidates}
          selectedLessonIds={selectedLessonIds}
          editable={editable}
          onChange={onRecommendationsChange}
        />
      </div>
    </section>
  )
}
